import { forwardRef, type ReactNode, type TextareaHTMLAttributes } from 'react';
import { WarningCircle } from '@phosphor-icons/react';
import { cx } from '../cx';
import { Field, fieldControlClass } from '../Field/Field';
import { Icon } from '../Icon';

export type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  label: ReactNode;
  labelHidden?: boolean;
  help?: ReactNode;
  error?: ReactNode;
  fieldClassName?: string;
};

/**
 * A multi-line text field that looks like `Input`: the surface colour, a 3:1 outline, 16px text (so iOS never
 * zooms), a visible label and errors that say how to fix them. Grows from three rows; the person can resize it
 * vertically. For text that reads as running text where it sits and is edited in place, build on the page instead.
 */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, labelHidden, help, error, id, className, fieldClassName, rows = 3, ...rest },
  ref,
) {
  return (
    <Field label={label} labelHidden={labelHidden} help={help} error={error} id={id} className={fieldClassName}>
      {({ id: fieldId, describedBy, invalid }) => (
        <div className="relative">
          <textarea
            {...rest}
            ref={ref}
            id={fieldId}
            rows={rows}
            aria-invalid={invalid || undefined}
            aria-describedby={cx(describedBy, rest['aria-describedby']) || undefined}
            className={cx(
              // The shared control class is a fixed 48px; a textarea sizes from its rows instead.
              fieldControlClass(invalid).replace(/\bh-12\b/, 'h-auto min-h-12 py-3 resize-y leading-6'),
              invalid && 'pr-11',
              className,
            )}
          />
          {invalid ? (
            <span className="pointer-events-none absolute right-4 top-3 flex items-center text-jc-ink">
              <Icon icon={WarningCircle} size="md" weight="fill" />
            </span>
          ) : null}
        </div>
      )}
    </Field>
  );
});
